import {
    Children,
    createElement as createReactElement,
    type CSSProperties,
    Fragment,
    type ReactElement,
} from 'react'

import type { doisters } from '@/components/inline-avatar-stack/doister-avatars'
import { InlineAvatarStack } from '@/components/inline-avatar-stack/inline-avatar-stack'
import { PhoneFrame, type PhoneType } from '@/components/PhoneFrame/PhoneFrame'
import { BlockQuote } from '@/components/Text/BlockQuote'
import { Button } from '@/components/Button/Button'
import { Hairline } from '@/components/Hairline/Hairline'
import { Link } from '@/components/Link/Link'
import type { Themes } from '@/Theme'
import { rewriteUrl as rewriteHelpCenterUrl } from '@/utils/helpCenterUrlRewriter'

import type { IconProps } from './components/Icon/Icon'
import type { HCImageProps } from './components/Image/Image'
import {
    Accordion,
    Code,
    Em,
    Heading,
    Icon,
    IFrame,
    Image,
    Italic,
    List,
    Note,
    Paragraph,
    Platforms,
    Pre,
    Space,
    Strong,
    Table,
} from './components/index'
import { isChildrenContainsIframe, isChildrenEmpty } from './utils/sanitizers'

import type { InlineAvatarProps } from '@/components/inline-avatar-stack/inline-avatar-stack'
import { Badge as MarketistBadge } from '@/components/Badge/Badge'

type Headings = 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
type ListTypes = 'ul' | 'ol'

type ElementTypes =
    | Headings
    | ListTypes
    | 'span'
    | 'em'
    | 'i'
    | 'br'
    | 'strong'
    | 'code'
    | 'pre'
    | 'hr'
    | 'a'
    | 'p'
    | 'table'
    | 'iframe'
    | 'img'
    | 'button'
    | 'div'
    | 'blockquote'

type CreateElementProps = {
    key?: string
    style?: CSSProperties
    src?: string
    href?: string
    className?: string
    'data-accordion'?: string
    'data-icon'?: string
    'data-inline'?: string
    'data-box-name'?: string
    'data-box-avatar'?: string
    'data-box-description'?: string
    [key: string]: unknown
}

/** Filters and render our own components */
export function createElement(
    selectedPlatform: string | undefined,
    setSelectedPlatform: (platform: string) => void,
    theme: Themes = 'todoist',
) {
    // The first image we encounter should not be lazyload.
    let isFirstImageProcessed = false

    return function createElementWithContext(
        type: ElementTypes,
        props: object | undefined = {},
        // biome-ignore lint/suspicious/noExplicitAny: I don't know how to type this properly
        ...rest: any
    ): JSX.Element {
        const { key, ...properties } = props as CreateElementProps

        /** First, sanitize the Zendesk HTML markup */

        /** Remove empty spaces */
        let children = Children.toArray(rest).filter((child) => {
            if (typeof child === 'string') {
                return child.trim().length > 0
            }
            return true
        })

        /** Remove useless <span>s from Zendesk */
        if (type === 'span' && properties.style && !properties.style.backgroundColor) {
            return <Fragment key={key}>{children}</Fragment>
        }

        /** Remove useless divs */
        if (
            type === 'div' &&
            (properties.className?.includes('wysiwyg-') || Object.keys(properties).length === 0)
        ) {
            return <Fragment key={key}>{children}</Fragment>
        }

        /** Fix images to use absolute urls */
        if (properties?.src?.startsWith('/hc') || properties?.src?.startsWith('/guide-media')) {
            properties.src = `https://get.todoist.help${properties.src}`
        }

        /** Next, map HTML elements to React components */

        if (type === 'em') {
            return (
                <Em key={key} {...properties}>
                    {children}
                </Em>
            )
        }

        if (type === 'i') {
            return (
                <Italic key={key} {...properties}>
                    {children}
                </Italic>
            )
        }

        if (type === 'br') {
            return (
                <Space key={key} {...properties}>
                    {children}
                </Space>
            )
        }

        if (type === 'strong') {
            return (
                <Strong key={key} {...properties}>
                    {children}
                </Strong>
            )
        }

        if (type === 'code') {
            return (
                <Code key={key} {...properties}>
                    {children}
                </Code>
            )
        }

        if (type === 'pre') {
            return (
                <Pre key={key} {...properties}>
                    {children}
                </Pre>
            )
        }

        if (['h1', 'h2', 'h3', 'h4', 'h5'].includes(type)) {
            return (
                <Heading key={key} tag={type as Headings} theme={theme} {...properties}>
                    {children}
                </Heading>
            )
        }

        if (type === 'hr') {
            return <Hairline key={key} {...properties} />
        }

        if (type === 'a') {
            if (properties.href) {
                properties.href = rewriteHelpCenterUrl(properties.href)
            }

            if (properties.className?.includes('primary-button')) {
                return (
                    <Button
                        style={{ marginBlockStart: 'var(--space-8)' }}
                        key={key}
                        {...properties}
                    >
                        {children}
                    </Button>
                )
            }

            return (
                <Link size="inherit" key={key} {...properties}>
                    {children}
                </Link>
            )
        }

        if (type === 'p') {
            // There is a lot of empty `<p>` tags. Do not render them.
            if (isChildrenEmpty(children)) {
                return <Fragment key={key} />
            }
            // iframes are contained in `<p>` (enforced by Zendesk formatting) but
            // our `IFrame` component is already surrounding the iframe with a `<div>`,
            // causing hydration errors. Skip rendering `<p>` and directly the sub-components.
            if (isChildrenContainsIframe(children)) {
                return <Fragment key={key}>{children}</Fragment>
            }
            return (
                <Paragraph key={key} {...properties}>
                    {children}
                </Paragraph>
            )
        }

        if (type === 'table') {
            return (
                <Table key={key} {...properties}>
                    {children}
                </Table>
            )
        }

        if (['ul', 'ol'].includes(type)) {
            return (
                <List key={key} tag={type as ListTypes} {...properties}>
                    {children}
                </List>
            )
        }

        if (type === 'iframe') {
            return (
                <IFrame key={key} {...properties}>
                    {children}
                </IFrame>
            )
        }

        if (type === 'button') {
            if (properties.href) {
                properties.href = rewriteHelpCenterUrl(properties.href)
            }
            return (
                <Button style={{ marginBlockStart: 'var(--space-8)' }} key={key} {...properties}>
                    {children}
                </Button>
            )
        }

        if (type === 'blockquote') {
            return (
                <BlockQuote className="mbs-32" key={key} {...properties}>
                    {children}
                </BlockQuote>
            )
        }

        /** Custom components */

        if (typeof properties['data-accordion'] === 'string') {
            return (
                <Accordion key={key} theme={theme} {...properties}>
                    {children}
                </Accordion>
            )
        }

        if (typeof properties['data-platforms'] === 'string') {
            return (
                <Platforms
                    key={key}
                    selectedPlatform={selectedPlatform}
                    onSelectPlatform={setSelectedPlatform}
                    {...properties}
                >
                    {children}
                </Platforms>
            )
        }

        if (properties?.className?.includes('box-tip')) {
            // find elements with `data-box-xx` attribute
            // and remove them from the children
            let description: ReactElement | undefined
            let badge: ReactElement | undefined
            children = children?.filter((child) => {
                if (typeof child === 'object' && 'props' in child) {
                    if (typeof child?.props?.['data-box-description'] === 'string') {
                        description = child
                        return false
                    }

                    if (typeof child?.props?.['data-box-badge'] === 'string') {
                        badge = child
                        return false
                    }
                }

                return true
            })
            const name = properties['data-box-name']
            const avatar = properties['data-box-avatar'] as keyof typeof doisters
            const role = description?.props.children
            const Badge = badge && (
                <MarketistBadge variant={badge.props['data-box-badge'] || 'helpCenter'}>
                    {badge.props.children}
                </MarketistBadge>
            )

            return (
                <Note
                    key={key}
                    variant="tip"
                    name={name}
                    description={role}
                    avatar={avatar}
                    badge={Badge}
                >
                    {children}
                </Note>
            )
        }

        if (properties?.className?.includes('box-note')) {
            return (
                <Note key={key} variant="note">
                    {children}
                </Note>
            )
        }

        if (properties?.className?.includes('box-warning')) {
            return (
                <Note key={key} variant="warning">
                    {children}
                </Note>
            )
        }

        if (type === 'span' && typeof properties['data-inline-avatars'] === 'string') {
            const doisters = properties['data-inline-avatars'].split(
                ' ',
            ) as InlineAvatarProps['doister'][]

            if (!doisters.length) return <Fragment key={key} />

            return (
                <InlineAvatarStack>
                    {doisters.map((doister) => (
                        <InlineAvatarStack.Avatar key={doister} doister={doister} />
                    ))}
                </InlineAvatarStack>
            )
        }

        if (type === 'span' && typeof properties['data-inline-avatar-urls'] === 'string') {
            const urls = properties['data-inline-avatar-urls'].split(' ')

            if (!urls.length) return <Fragment key={key} />

            return (
                <InlineAvatarStack>
                    {urls.map((url) => (
                        <InlineAvatarStack.Avatar key={url} src={url} />
                    ))}
                </InlineAvatarStack>
            )
        }

        if (type === 'span' && properties['data-icon'] !== undefined) {
            const iconProps = properties as unknown as IconProps
            return <Icon key={key} name={properties['data-icon']} {...iconProps} />
        }

        if (type === 'img' && properties['data-phone-frame'] !== undefined) {
            return (
                <PhoneFrame
                    key={key}
                    type={properties['data-phone-frame'] as PhoneType}
                    {...properties}
                />
            )
        }

        if (type === 'img') {
            if (properties['data-icon'] !== undefined || properties['data-inline'] !== undefined) {
                const iconProps = properties as unknown as IconProps
                return <Icon key={key} name={properties['data-icon']} {...iconProps} />
            }

            const shouldLazyLoad = isFirstImageProcessed
            isFirstImageProcessed = true

            return (
                <Image
                    lazyload={shouldLazyLoad}
                    key={key}
                    {...(properties as unknown as HCImageProps)}
                />
            )
        }

        return createReactElement(
            type as string,
            { key, ...properties },
            ...(children as React.ReactNode[]),
        )
    }
}
